// ─────────────────────────────────────────────────────────────────────────────
// Buckly's card on the Trading Overview — the design's own card for her
// (Trading Homepage.dc.html:111-125), in place of her label-only tile (U6). It is
// the ONLY card that changes: the view tiles, Arthur, Mack, Wallace, the service
// and code-team cards and the body below keep their own code.
//
// Two things the design's page gave it for free are restated here:
//   • it was an <a> on a page whose type is Inter; here it is a <button> (the
//     desktop routes by roster, through the same onAgentClick as before), so the
//     button's own font, colour and centring are reset to what the link inherited;
//   • it was a 240px-basis flex item; the desktop's row is a grid of ~150px
//     cells, so it spans two cells — at one cell "Psychology" cannot fit.
// "preview" is the design's copy and stays as drawn (R21). "State:" is her Your State reading (B-4.1b;
// its manual's §9): the state label (or "not set") and readiness (or "—"); drawn as designed until that
// manual is current in her store.
//
// KNOWN, AS DESIGNED: the mark is an opaque PNG on black that relies on
// mix-blend-mode:screen. The card's backdrop-filter isolates it, so the black can
// only blend with the card's own dark fill — invisible on the design's near-black
// hero, a dark square on the desktop's lighter Overview. Left as drawn (B-1 report).
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ASSET, Hover, sx } from './primitives';
import { DESIGN_GROUND, PAGE_TEXT, SANS } from './tokens';
import { panel, useBucklyToday } from './todayApi';

const LINK_INHERITED: React.CSSProperties = {
  ...DESIGN_GROUND,
  fontFamily: SANS, fontSize: '16px', color: PAGE_TEXT, textAlign: 'left',
  cursor: 'pointer', gridColumn: 'span 2', width: '100%',
};

export default function BucklyOverviewCard({ onClick }: { onClick: () => void }) {
  const ys = panel(useBucklyToday(), 'your_state');
  const r = ys ? ys.reading : null;
  const v = {
    cardDrawn: !ys, cardLive: !!ys,
    cardState: `${r && r.state_label && r.state_label !== 'UNKNOWN' ? r.state_label : 'not set'} · `
      + `${r && typeof r.readiness_pct === 'number' ? `${r.readiness_pct}%` : '—'}`,
  };
  return (
    // ⟨design Trading Homepage.dc.html:111-125 (bound copy: b4/s2/ui/assemble_s2.py) — generated by dc2tsx.py; regenerate, never hand-edit⟩
    <Hover as="button" type="button" onClick={onClick} style={{ ...LINK_INHERITED, ...sx("flex:1 1 240px; min-width:0; display:flex; align-items:center; gap:16px; padding:17px 20px; border-radius:14px; text-decoration:none; background:linear-gradient(180deg, rgba(14,19,24,0.62) 0%, rgba(9,12,15,0.7) 100%); backdrop-filter:blur(16px); -webkit-backdrop-filter:blur(16px); border:1px solid rgba(150,205,240,0.16); box-shadow:inset 0 1px 0 rgba(255,255,255,0.05), 0 14px 40px rgba(0,0,0,0.45), 0 0 30px rgba(150,205,240,0.05); transition:transform .35s cubic-bezier(.2,.7,.2,1), border-color .35s, box-shadow .35s;") }} hover={sx("transform:translateY(-4px); border-color:rgba(150,205,240,0.5); box-shadow:inset 0 1px 0 rgba(255,255,255,0.07), 0 18px 50px rgba(0,0,0,0.5), 0 0 48px rgba(150,205,240,0.17);")}>
      <img src={ASSET["buckly-mark.png"]} alt="Buckly" style={sx("flex:none; width:66px; height:66px; object-fit:contain; mix-blend-mode:screen; display:block;")} />
      <div style={sx("min-width:0;")}>
        <div style={sx("display:flex; align-items:center; gap:9px; margin-bottom:3px;")}>
          <span style={sx("font-size:20px; font-weight:500; color:#f3f7f8;")}>Buckly</span>
          <span style={sx("display:inline-flex; align-items:center; gap:5px; font-size:11.5px; color:#a9dcf7;")}>
            <span style={sx("width:6px; height:6px; border-radius:50%; background:#96cdf0;")} />
            preview
          </span>
          <span style={sx("margin-left:auto; font-size:14px; color:rgba(169,220,247,0.7);")}>→</span>
        </div>
        <div style={sx("font-size:13.5px; color:rgba(226,232,234,0.58); margin-bottom:7px;")}>Psychology</div>
        <div style={sx("font-size:13.5px; color:rgba(220,228,230,0.55);")}>
          {"State: "}
          <span style={sx("color:#a9dcf7;")}>
            {v.cardDrawn && (
              <>
                Guarded · 56%
              </>
            )}
            {v.cardLive && (
              <>
                {v.cardState}
              </>
            )}
          </span>
        </div>
      </div>
    </Hover>
    // ⟨/design⟩
  );
}
